import React, { useEffect, useRef } from "react";
import { useReveal } from "../hooks/useReveal";
import trackinLogo from "../logo/products/trackin.png";
import kronLogo from "../logo/products/super-agent.png";
import motoMeshLogo from "../logo/products/moto-mesh.png";
import fleettrackLogo from "../logo/products/fleettrack.png";

const PRODUCTS = [
  { name: "TrackIn", logo: trackinLogo, url: "https://play.google.com/store/apps/details?id=in.co.fleettrack&hl=en_IN", desc: "Fleet and fuel management, rebuilt from what the research showed, not from what the market was already selling." },
  { name: "Moto Mesh", logo: motoMeshLogo, url: "https://play.google.com/store/apps/details?id=in.gpstrack.rider&hl=en_IN", desc: "A mobile app for riders to track each other in real time, get SOS alerts, and share ride stats and history. Built for groups, clubs, and touring communities." },
  { name: "Fleettrack", logo: fleettrackLogo, url: "https://play.google.com/store/apps/details?id=in.gpstrack.lite&hl=en_IN", desc: "GPS tracking research applied to the field, where knowing a vehicle's real position, honestly, was the broken thing worth fixing." },
  { name: "Kron", logo: kronLogo, wip: true },
];

const ProductsSection = () => {
  const [headerRef, headerIn] = useReveal();
  const railRef = useRef(null);
  const trackRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    const rail = railRef.current;
    const track = trackRef.current;
    if (!rail || !track) return undefined;

    const cards = [...track.children];
    const step = () => (cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : 0);

    // center-focus scaling, recomputed as the rail scrolls (finite — clamps at both ends)
    let rafPending = false;
    const updateScale = () => {
      rafPending = false;
      const mid = rail.scrollLeft + rail.clientWidth / 2;
      cards.forEach((card) => {
        const cc = card.offsetLeft + card.offsetWidth / 2;
        const dist = Math.min(Math.abs(cc - mid) / 380, 1);
        card.style.transform = `scale(${(1 - dist * 0.16).toFixed(3)})`;
        card.style.opacity = (1 - dist * 0.5).toFixed(3);
      });
    };
    const onScroll = () => { if (!rafPending) { rafPending = true; window.requestAnimationFrame(updateScale); } };
    const onPrev = () => rail.scrollBy({ left: -step(), behavior: "smooth" });
    const onNext = () => rail.scrollBy({ left: step(), behavior: "smooth" });
    const onResize = () => updateScale();

    updateScale();
    rail.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    prevRef.current.addEventListener("click", onPrev);
    nextRef.current.addEventListener("click", onNext);

    return () => {
      rail.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (prevRef.current) prevRef.current.removeEventListener("click", onPrev);
      if (nextRef.current) nextRef.current.removeEventListener("click", onNext);
    };
  }, []);

  return (
    <section id="products" className="relative py-24 sm:py-32 overflow-hidden" style={{ background: "linear-gradient(180deg,#F4EFFB,#FCFAFF 45%)" }}>
      <div ref={headerRef} className={`reveal text-center max-w-2xl mx-auto px-5 sm:px-8 ${headerIn ? "in" : ""}`}>
        <span className="kicker text-violet">Our Products</span>
        <h2 className="mt-5 font-display font-normal text-4xl sm:text-5xl tracking-tight leading-[1.06]">
          The loop, <span className="italic text-rose">shipped.</span>
        </h2>
        <p className="mt-5 text-lg text-ink/60 font-normal leading-relaxed">
          Every one of these began as friction we experienced. This is what came out the other side.
        </p>
      </div>
      <div className="pf-wrap">
        <button ref={prevRef} className="pf-nav pf-prev" aria-label="Previous products">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div ref={railRef} className="pf-rail">
          <div ref={trackRef} className="pf-track">
            {PRODUCTS.map((product) => (
              <div key={product.name} className={`pf-card ${product.wip ? "pf-card-wip" : ""}`}>
                <img className="pf-lo" src={product.logo} alt={product.name} />
                <div className="pf-nm">
                  {product.name}
                  {product.wip && <span className="pf-wip">In progress</span>}
                </div>
                {product.wip ? (
                  <>
                    <p className="pf-ds pf-teaser">An AI trading strategy lab, in the making.</p>
                    <span className="pf-va pf-soon">Coming soon</span>
                  </>
                ) : (
                  <>
                    <p className="pf-ds">{product.desc}</p>
                    <a className="pf-va" href={product.url} target="_blank" rel="noopener noreferrer">
                      View app <span>&#8599;</span>
                    </a>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
        <button ref={nextRef} className="pf-nav pf-next" aria-label="Next products">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </section>
  );
};

export default ProductsSection;